import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { Hit } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { ReviewPendingOrdersRowData } from "../../../../data-model/tablesData";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import {
  MedicalOrderReviewStatus,
  MedicalOrderStatus,
} from "../../../../enums/orderReviewStatus";
import { ReviewPendingOrdersTableColumnKeys } from "../../../../enums/tableColumnKeys";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";
import { generateFullName } from "../../../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../../../utils/stringModifications";
import { getOrderIcons } from "../../medicalPaOrdersAlgolia/utils/getOrderIcons";
import { MedicalPaOrdersAlgoliaSortFields } from "../../medicalPaOrdersAlgolia/utils/medicalPaOrdersTable";

export const ReviewPendingOrdersTableColumns: TableHeader[] = [
  {
    label: "Patient Name",
    key: ReviewPendingOrdersTableColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 11,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.FIRST_NAME,
  },
  {
    label: "Date of Creation",
    key: ReviewPendingOrdersTableColumnKeys.CREATED_AT,
    order: 2,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.CREATED_AT,
  },
  {
    label: "Date of Work",
    key: ReviewPendingOrdersTableColumnKeys.DATE_OF_WORK,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_WORK,
  },
  {
    label: "Plan Details",
    key: ReviewPendingOrdersTableColumnKeys.PLAN_DETAILS,
    order: 3,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
    sortField: MedicalPaOrdersAlgoliaSortFields.PRIMARY_PAYER_NAME,
  },
  {
    label: "Medication",
    key: ReviewPendingOrdersTableColumnKeys.MEDICATION,
    order: 4,
    width: 18,
    sortable: true,
    type: TableCellType.STRING,
    sortField: MedicalPaOrdersAlgoliaSortFields.REGIMEN_NAME,
  },

  {
    label: "DOS",
    key: ReviewPendingOrdersTableColumnKeys.DATE_OF_SERVICE,
    order: 6,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
    sortField: MedicalPaOrdersAlgoliaSortFields.DATE_OF_SERVICE,
  },
  {
    label: "BO Value",
    key: ReviewPendingOrdersTableColumnKeys.BO_VALUE,
    order: 7,
    width: 7,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: ReviewPendingOrdersTableColumnKeys.AUTH_VERIFICATION,
    order: 8,
    width: 9,
    type: TableCellType.BADGE,
  },
  {
    label: "",
    key: ReviewPendingOrdersTableColumnKeys.RPA_STATUS,
    order: 10,
    width: 3,
    type: TableCellType.RPA_STATUS,
  },
  {
    label: "",
    key: ReviewPendingOrdersTableColumnKeys.RPA_STATUS_TWO,
    order: 11,
    width: 3,
    type: TableCellType.RPA_STATUS_TWO,
  },
  {
    label: "",
    key: ReviewPendingOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS,
    order: 11,
    width: 6,
    type: TableCellType.OVERLAPPING_ICONS_WITH_POPOVER_AND_ACTIONS,
  },
];

/**
 * Transforms raw Postgres hit records into table row objects consumable by CustomTable.
 * Maps patient details, dates, plan info, medication, auth status badges, RPA statuses,
 * and review/order icons into the Review Pending Orders column schema.
 *
 * @remarks DOS, creation, and date-of-work columns use {@link formatDateToUTCWithFormat}
 *   on `*_iso` fields so calendar days match UTC-backed API values in every viewer timezone.
 *
 * @param algoliaRecords - Array of hit records from the Postgres response
 * @param authStatusOptions - Available auth status options for badge rendering
 * @returns Array of table row data objects keyed by ReviewPendingOrdersTableColumnKeys
 */
export const generateReviewPendingOrdersTableDataFromAlgoliaRecords = (
  algoliaRecords: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
): ReviewPendingOrdersRowData[] => {
  return algoliaRecords.map((algoliaRecord) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      algoliaRecord?.auth_status,
    );

    return {
      [ReviewPendingOrdersTableColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(
            algoliaRecord?.first_name ?? "",
            "",
            algoliaRecord?.last_name ?? "",
          ),
        ),
        secondaryText: `MRN : ${algoliaRecord?.patient_id ?? ""}`,
      },
      [ReviewPendingOrdersTableColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          algoliaRecord?.date_of_service_iso ?? "",
          "MM/DD/YYYY",
          "",
        ),
      [ReviewPendingOrdersTableColumnKeys.PLAN_DETAILS]: {
        mainText: algoliaRecord?.primary_payer_name ?? "",
        secondaryText: algoliaRecord?.primary_member_id ?? "",
      },
      [ReviewPendingOrdersTableColumnKeys.MEDICATION]:
        algoliaRecord?.regimen_name ?? "",

      [ReviewPendingOrdersTableColumnKeys.ROW_DATA]:
        JSON.stringify(algoliaRecord),
      [ReviewPendingOrdersTableColumnKeys.ID]: algoliaRecord?.id ?? "",
      [ReviewPendingOrdersTableColumnKeys.CREATED_AT]:
        formatDateToUTCWithFormat(
          algoliaRecord?.created_at_iso ?? "",
          "MM/DD/YYYY",
          "",
        ),
      [ReviewPendingOrdersTableColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
      },

      [ReviewPendingOrdersTableColumnKeys.BO_VALUE]: {
        text: algoliaRecord?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },

      [ReviewPendingOrdersTableColumnKeys.DATE_OF_WORK]:
        getDateOfWork(algoliaRecord),

      [ReviewPendingOrdersTableColumnKeys.RPA_STATUS]: {
        status:
          algoliaRecord?.ev_write_back_status ??
          EVWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },

      [ReviewPendingOrdersTableColumnKeys.RPA_STATUS_TWO]: {
        status:
          algoliaRecord?.document_upload_status ??
          DocWriteBackStatus.NOT_INITIATED,
        shouldPropagate: true,
        id: algoliaRecord?.id ?? "",
        masterAuthStatus: algoliaRecord?.master_auth_status ?? "",
      },
      [ReviewPendingOrdersTableColumnKeys.ORDER_PROCESS_AND_REVIEW_STATUS]: {
        ...getOrderIcons(
          algoliaRecord?.medical_order_status as MedicalOrderStatus,
          algoliaRecord?.medical_order_review_status as MedicalOrderReviewStatus,
          algoliaRecord?.master_auth_status === "query" &&
            algoliaRecord?.ai_agent_type === "agent",
        ),
        order_id: algoliaRecord?.id ?? "",
      },
    };
  });
};

/**
 * Extracts and formats the date of work from a hit record (UTC calendar day).
 *
 * @param algoliaRecord - The hit record containing date_of_work_iso
 * @returns Formatted date string (MM/DD/YYYY) or "N/A" when missing or invalid
 */
const getDateOfWork = (algoliaRecord: Hit) => {
  const dateOfWork = algoliaRecord?.date_of_work_iso;
  if (dateOfWork) {
    return formatDateToUTCWithFormat(dateOfWork, "MM/DD/YYYY", "N/A");
  }
  return "N/A";
};
